import { Link } from 'react-router-dom';

/**
 * Privacy Policy. Plain-language summary of what the app stores and why,
 * matching the data the backend actually handles (see RUNBOOK launch
 * prerequisites). Wording is a starting draft — have it reviewed for your
 * jurisdiction before relying on it.
 */
export default function Privacy() {
  return (
    <section data-testid="page-privacy" className="prose-stone mx-auto max-w-prose">
      <h1 className="text-2xl font-bold text-stone-900">Privacy Policy</h1>
      <p className="mt-1 text-sm text-stone-500">Last updated 12 June 2026</p>

      <p className="mt-4 text-stone-700">
        Scrabble Daily is a free daily word game. This policy explains what we collect, why, and the
        choices you have. We keep it deliberately small: we only store what the game needs to work.
      </p>

      <h2 className="mt-6 text-lg font-bold text-stone-900">What we collect</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-stone-700">
        <li>
          <strong>Account details</strong> — your email address and a display name. If you sign in
          with Google, we receive your name and email from Google; we do not receive your password.
        </li>
        <li>
          <strong>A public leaderboard name</strong> — an auto-generated pseudonym (e.g. “Maple
          Quail #6904”). Your real name and email are never shown on leaderboards.
        </li>
        <li>
          <strong>Gameplay data</strong> — the moves, scores, and results of the daily puzzles you
          play, used for your history, streaks, and leaderboard ranking.
        </li>
      </ul>

      <h2 className="mt-6 text-lg font-bold text-stone-900">Cookies</h2>
      <p className="mt-2 text-stone-700">
        We use one essential, secure, HTTP-only cookie to keep you signed in (a refresh token). We
        also set a cookie (<code>sdc_guest</code>) recording which of today&apos;s boards you have
        started, before you create an account. It contains only a date and a difficulty — no
        identifier — and expires after 48 hours. We do not use advertising or third-party tracking
        cookies.
      </p>

      <h2 className="mt-6 text-lg font-bold text-stone-900">Email</h2>
      <p className="mt-2 text-stone-700">
        We send account email — a six-digit code to verify your address and password-reset codes —
        via Amazon Simple Email Service (SES). If you're opted in, we also send optional gameplay
        emails: occasional reminders when you haven't played, and a daily results digest of the top
        boards on the levels you played. Every optional email has a one-click unsubscribe, and you
        can manage both from{' '}
        <Link to="/settings" className="font-semibold text-emerald-700 underline">
          Settings
        </Link>
        . We do not send marketing email.
      </p>

      <h2 className="mt-6 text-lg font-bold text-stone-900">Who we share it with</h2>
      <p className="mt-2 text-stone-700">
        We do not sell your data. We share it only with the service providers that run the game:
        hosting and database (Railway), website delivery (Cloudflare), email (Amazon SES), and
        Google (only if you choose Google sign-in). Error monitoring (Sentry) may record diagnostic
        data, excluding message contents.
      </p>

      <h2 className="mt-6 text-lg font-bold text-stone-900">Keeping and deleting your data</h2>
      <p className="mt-2 text-stone-700">
        You can delete your account at any time from{' '}
        <Link to="/settings" className="font-semibold text-emerald-700 underline">
          Settings
        </Link>
        , which removes your account and personal data. We keep data only as long as your account
        exists, apart from minimal backups retained for up to 30 days for recovery.
      </p>

      <h2 className="mt-6 text-lg font-bold text-stone-900">Contact</h2>
      <p className="mt-2 text-stone-700">
        Questions about this policy or your data? Email{' '}
        <a href="mailto:waltersn63@gmail.com" className="font-semibold text-emerald-700 underline">
          waltersn63@gmail.com
        </a>
        .
      </p>

      <p className="mt-6 text-sm text-stone-500">
        See also our{' '}
        <Link to="/terms" className="font-semibold text-emerald-700 underline">
          Terms of Service
        </Link>
        .
      </p>
    </section>
  );
}
